import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Send, Loader2, User, FileText, Paperclip, Calendar, ChevronDown } from 'lucide-react';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const TEMPLATE_OPTIONS = [
  { value: 'payment_request', label: 'Payment Request' },
  { value: 'quotation', label: 'Quotation' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'vendor_payment', label: 'Vendor Payment' },
  { value: 'project_update', label: 'Project Update' },
  { value: 'boq', label: 'BOQ' },
];

const DEFAULT_TEMPLATES = {
  payment_request: {
    subject: 'Payment Request - {{project_name}} | Bella Casa Interiors',
    body: `Dear {{client_name}},

Greetings from Bella Casa Interiors!

This is a gentle reminder regarding the pending payment for your project "{{project_name}}".

Payment Details:
- Project: {{project_name}}
- Contract Value: {{contract_value}}
- Total Paid: {{total_paid}}
- Balance Due: {{balance_due}}

Please find the payment request for your reference. Kindly process the payment at the earliest.

If you have already made the payment, please share the payment confirmation for our records.

For any queries, please feel free to reach out to us.

Thank you for your continued trust in us.

Best regards,
{{sender_name}}
Bella Casa Interiors`,
  },
  quotation: {
    subject: 'Quotation - {{project_name}} | Bella Casa Interiors',
    body: `Dear {{client_name}},

Please find attached the quotation for your project "{{project_name}}".

We have prepared this quotation based on the scope discussed. Kindly review and let us know if you need any changes.

Best regards,
{{sender_name}}
Bella Casa Interiors`,
  },
  invoice: {
    subject: 'Invoice - {{project_name}} | Bella Casa Interiors',
    body: `Dear {{client_name}},

Please find the invoice for your project "{{project_name}}".

Kindly process the payment at the earliest convenience.

Best regards,
{{sender_name}}
Bella Casa Interiors`,
  },
  vendor_payment: {
    subject: 'Payment Confirmation - {{project_name}} | Bella Casa Interiors',
    body: `Dear Vendor,

This is to confirm the payment details for project "{{project_name}}".

Please verify the amount and acknowledge receipt.

Best regards,
{{sender_name}}
Bella Casa Interiors`,
  },
  project_update: {
    subject: 'Project Update - {{project_name}} | Bella Casa Interiors',
    body: `Dear {{client_name}},

Here is a progress update for your project "{{project_name}}".

Project Status:
- Overall Progress: {{progress_percentage}}% ({{completed_tasks}} of {{total_tasks}} tasks completed)
- Tasks In Progress: {{in_progress_tasks}}
- Current Phase: {{current_phase}}
- Contract Value: {{contract_value}}
- Amount Paid: {{total_paid}}
- Balance Due: {{balance_due}}

For detailed task status, please visit your project dashboard.

Best regards,
{{sender_name}}
Bella Casa Interiors`,
  },
  boq: {
    subject: 'BOQ - {{project_name}} | Bella Casa Interiors',
    body: `Dear {{client_name}},

Please find the Bill of Quantities (BOQ) for your project "{{project_name}}".

The BOQ includes a detailed breakdown of all items, quantities, and rates. You can also view the full BOQ online using the link provided.

For any queries or changes, please don't hesitate to reach out.

Best regards,
{{sender_name}}
Bella Casa Interiors`,
  },
};

function getDefaultDates() {
  const today = new Date();
  const fyStart = new Date(today.getFullYear(), today.getMonth() >= 3 ? 3 : -9, 1);
  return {
    from: fyStart.toISOString().split('T')[0],
    to: today.toISOString().split('T')[0],
  };
}

export default function EmailComposeDialog({ open, onOpenChange, projectId, templateType = 'payment_request' }) {
  const [selectedTemplate, setSelectedTemplate] = useState(templateType);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);

  // Statement attachment state
  const [attachStatement, setAttachStatement] = useState(false);
  const defaults = getDefaultDates();
  const [statementFrom, setStatementFrom] = useState(defaults.from);
  const [statementTo, setStatementTo] = useState(defaults.to);

  useEffect(() => {
    if (open && projectId) {
      setSelectedTemplate(templateType);
      loadContext(templateType);
      const d = getDefaultDates();
      setStatementFrom(d.from);
      setStatementTo(d.to);
      setAttachStatement(false);
    }
  }, [open, projectId, templateType]);

  const handleTemplateChange = (newType) => {
    setSelectedTemplate(newType);
    loadContext(newType);
  };

  const loadContext = async (tmplType) => {
    setLoading(true);
    try {
      const [contextRes, templateRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/email-context`),
        axios.get(`${API}/email-templates/${tmplType}`).catch(() => null)
      ]);

      const ctx = contextRes.data;
      setTo(ctx.client_email || '');

      const tmpl = templateRes?.data || DEFAULT_TEMPLATES[tmplType] || DEFAULT_TEMPLATES.payment_request;
      setSubject(fillTemplate(tmpl.subject, ctx));
      setBody(fillTemplate(tmpl.body, ctx));
    } catch {
      const tmpl = DEFAULT_TEMPLATES[tmplType] || DEFAULT_TEMPLATES.payment_request;
      setSubject(tmpl.subject);
      setBody(tmpl.body);
    } finally {
      setLoading(false);
    }
  };

  const fillTemplate = (text, ctx) => {
    const fl = ctx.fund_ledger || {};
    const pg = ctx.progress || {};
    return text
      .replace(/\{\{project_name\}\}/g, ctx.project_name || '')
      .replace(/\{\{client_name\}\}/g, ctx.client_name || 'Client')
      .replace(/\{\{sender_name\}\}/g, ctx.sender_name || 'Admin')
      .replace(/\{\{company_name\}\}/g, ctx.company_name || 'Bella Casa Interiors')
      .replace(/\{\{contract_value\}\}/g, fl.contract_value ? `₹${fl.contract_value.toLocaleString('en-IN')}` : '')
      .replace(/\{\{total_paid\}\}/g, fl.total_paid ? `₹${fl.total_paid.toLocaleString('en-IN')}` : '₹0')
      .replace(/\{\{balance_due\}\}/g, fl.balance_due ? `₹${fl.balance_due.toLocaleString('en-IN')}` : '')
      .replace(/\{\{amount\}\}/g, fl.balance_due ? `₹${fl.balance_due.toLocaleString('en-IN')}` : '')
      .replace(/\{\{progress_percentage\}\}/g, pg.percentage ?? '0')
      .replace(/\{\{total_tasks\}\}/g, pg.total_tasks ?? '0')
      .replace(/\{\{completed_tasks\}\}/g, pg.completed ?? '0')
      .replace(/\{\{in_progress_tasks\}\}/g, pg.in_progress ?? '0')
      .replace(/\{\{current_phase\}\}/g, pg.current_phase || 'In progress');
  };

  const handleSend = async () => {
    if (!to.trim()) { toast.error('Recipient email is required'); return; }
    if (!subject.trim()) { toast.error('Subject is required'); return; }

    setSending(true);
    try {
      await axios.post(`${API}/send-composed-email`, {
        to: to.trim(),
        cc: cc.trim(),
        subject: subject.trim(),
        body: body.trim(),
        project_id: projectId,
        attach_statement: attachStatement,
        statement_from_date: attachStatement ? statementFrom : null,
        statement_to_date: attachStatement ? statementTo : null,
      });
      toast.success(attachStatement ? 'Email sent with customer statement!' : 'Email sent successfully!');
      onOpenChange(false);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send email';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const handleDownloadStatement = () => {
    if (!projectId) return;
    const token = localStorage.getItem('token');
    const url = `${API}/projects/${projectId}/client-statement-pdf?from_date=${statementFrom}&to_date=${statementTo}`;
    window.open(url + `&token=${token}`, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="email-compose-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <div className="p-1.5 rounded-lg bg-amber-100">
              <Mail size={18} className="text-amber-700" />
            </div>
            Compose Email
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12" data-testid="email-compose-loading">
            <Loader2 className="animate-spin text-stone-400" size={24} />
            <span className="ml-2 text-stone-500">Loading template...</span>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            {/* Template Type Selector */}
            <div className="space-y-1">
              <Label className="text-xs font-medium text-stone-500">Email Type</Label>
              <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
                <SelectTrigger className="h-9 border-stone-200" data-testid="email-template-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TEMPLATE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* To */}
            <div className="space-y-1">
              <Label className="text-xs font-medium text-stone-500">To</Label>
              <div className="flex items-center gap-2">
                <User size={14} className="text-stone-400 shrink-0" />
                <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="email@example.com, another@example.com" className="h-9 border-stone-200" data-testid="email-compose-to" />
              </div>
              <p className="text-xs text-stone-400 pl-6">Separate multiple emails with commas</p>
            </div>

            {/* CC */}
            <div className="space-y-1">
              <Label className="text-xs font-medium text-stone-500">CC <span className="text-stone-400">(optional)</span></Label>
              <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="cc@email.com, another@email.com" className="h-9 border-stone-200" data-testid="email-compose-cc" />
            </div>

            {/* Subject */}
            <div className="space-y-1">
              <Label className="text-xs font-medium text-stone-500">Subject</Label>
              <div className="flex items-center gap-2">
                <FileText size={14} className="text-stone-400 shrink-0" />
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject" className="h-9 border-stone-200 font-medium" data-testid="email-compose-subject" />
              </div>
            </div>

            <div className="border-t border-stone-200" />

            {/* Body */}
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write your email..." className="min-h-[220px] border-stone-200 text-sm leading-relaxed resize-y font-mono" data-testid="email-compose-body" />

            {/* Attachments Section */}
            <div className="border border-stone-200 rounded-lg p-4 space-y-3 bg-stone-50/50">
              <div className="flex items-center gap-2 text-sm font-medium text-stone-700">
                <Paperclip size={15} />
                Attachments
              </div>

              {/* Attach Customer Statement */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="attach-statement"
                    checked={attachStatement}
                    onCheckedChange={setAttachStatement}
                    data-testid="email-attach-statement-checkbox"
                  />
                  <label htmlFor="attach-statement" className="text-sm text-stone-700 cursor-pointer font-medium">
                    Attach Customer Statement (PDF)
                  </label>
                </div>

                {attachStatement && (
                  <div className="ml-6 p-3 bg-white border border-stone-200 rounded-md space-y-3">
                    <p className="text-xs text-stone-500">Select date range for the customer statement</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-stone-500">From Date</Label>
                        <div className="flex items-center gap-1.5">
                          <Calendar size={13} className="text-stone-400" />
                          <Input
                            type="date"
                            value={statementFrom}
                            onChange={(e) => setStatementFrom(e.target.value)}
                            className="h-8 text-xs border-stone-200"
                            data-testid="email-statement-from-date"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-stone-500">To Date</Label>
                        <div className="flex items-center gap-1.5">
                          <Calendar size={13} className="text-stone-400" />
                          <Input
                            type="date"
                            value={statementTo}
                            onChange={(e) => setStatementTo(e.target.value)}
                            className="h-8 text-xs border-stone-200"
                            data-testid="email-statement-to-date"
                          />
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs h-7 gap-1"
                      onClick={handleDownloadStatement}
                      data-testid="email-preview-statement"
                    >
                      <FileText size={12} /> Preview Statement PDF
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-2 border-t border-stone-100">
              <p className="text-xs text-stone-400">
                {attachStatement ? 'Email will include customer statement PDF' : 'Email will be sent via Bella Casa Interiors'}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="email-compose-cancel">Cancel</Button>
                <Button
                  size="sm"
                  onClick={handleSend}
                  disabled={sending || !to.trim()}
                  className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
                  data-testid="email-compose-send"
                >
                  {sending ? <><Loader2 size={14} className="animate-spin" /> Sending...</> : <><Send size={14} /> Send Email</>}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
